// High-verbosity, readable URL builder for affiliate providers stored in Sanity

import { getTripDomainForLanguage } from '../utils/trip';
import { wrapAffiliateLink } from '../proxy';

// Re-export Booking.com builder for convenient import
export {
    buildBookingLink,
    buildBookingLinkFromData,
    buildBookingCountryLink,
    resolveBookingCurrency,
    mapSiteLangToBookingLocale,
    trackBookingClick
} from './booking';

const toMap = (arr = [], keyProp = 'canonical', valProp = 'actual') => {
    const map = new Map();
    for (const item of arr) {
        if (item?.[keyProp]) map.set(item[keyProp], item[valProp]);
    }
    return map;
};

const appendQueryParams = (url, paramsObj) => {
    // Если url равен '#', возвращаем его как есть
    if (!url || url === '#') return '#';

    // Проверяем, что url валиден
    if (!/^https?:\/\//i.test(url)) {
        return url; // Возвращаем как есть, если не валидный URL
    }

    try {
        const u = new URL(url);
        for (const [k, v] of Object.entries(paramsObj)) {
            if (v == null) continue;
            u.searchParams.set(k, String(v));
        }
        return u.toString();
    } catch {
        // Если не удалось создать URL, возвращаем исходный url
        return url;
    }
};

export function buildAffiliateLink({
    provider,
    baseUrl,
    overrideUrl,
    context = {}, // e.g. { locale, currency, campaign, partnerId, from, to, citySlug, productId }
    subId,
}) {
    // Гигиена данных из Sanity: лишние пробелы в URL ломают new URL()/encodeURI
    // (пробел → %20 в пути → 404 у трекера). Триммим на входе, до любой обработки.
    if (typeof baseUrl === 'string') baseUrl = baseUrl.trim();
    if (typeof overrideUrl === 'string') overrideUrl = overrideUrl.trim();

    const wrap = (url) => provider?.code
        ? wrapAffiliateLink(url, { provider: provider.code, subId })
        : (url || '#');

    if (overrideUrl) return wrap(overrideUrl);
    if (!provider) return baseUrl || '#';

    const {
        linkMode = 'query',
        trackingBaseUrl,
        subIdParam = 'sub_id',
        defaultParams = [],
        paramMap = [],
        redirectBaseUrl,
        linkTemplates = [],
    } = provider;

    // Map canonical->actual
    const keyMap = toMap(paramMap || [], 'canonical', 'actual');
    const remapKey = (k) => keyMap.get(k) || k;

    const defaultsEntries = [];
    for (const { key, value } of defaultParams || []) {
        if (!key) continue;
        const actualKey = remapKey(key);
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed.startsWith('?')) {
                const rawQuery = trimmed.replace(/^\?+/, '');
                if (rawQuery.length > 0) {
                    const rawParams = new URLSearchParams(rawQuery);
                    if (Array.from(rawParams.keys()).length > 0) {
                        rawParams.forEach((paramValue, paramKey) => {
                            const mappedKey = remapKey(paramKey);
                            defaultsEntries.push([mappedKey, paramValue]);
                        });
                        continue;
                    }
                    defaultsEntries.push([actualKey, rawQuery]);
                    continue;
                }
                defaultsEntries.push([actualKey, '']);
                continue;
            }
        }
        defaultsEntries.push([actualKey, value]);
    }
    const defaultsObj = Object.fromEntries(defaultsEntries);
    const ctxObj = Object.fromEntries(Object.entries(context || {}).map(([k, v]) => [remapKey(k), v]));

    if (linkMode === 'shortFixed') {
        // отдаем baseUrl как есть, или trackingBaseUrl если baseUrl не передан
        return wrap(baseUrl || trackingBaseUrl || '#');
    }

    if (linkMode === 'template') {
        const template = linkTemplates?.[0]?.templateUrl;
        if (!template) return wrap(baseUrl || trackingBaseUrl || '#');
        let final = template;

        // Универсальная замена всех плейсхолдеров из контекста
        // Заменяем все плейсхолдеры вида {key} на значения из context
        const contextKeys = Object.keys(context || {});
        for (const key of contextKeys) {
            const value = context[key];
            if (value != null) {
                // Заменяем {key} и {keyName} (camelCase)
                const placeholder = `{${key}}`;
                final = final.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), String(value));
            }
        }

        // Также заменяем стандартные плейсхолдеры для обратной совместимости
        final = final
            .replace(/\{from\}/g, context.from || '')
            .replace(/\{to\}/g, context.to || '')
            .replace(/\{citySlug\}/g, context.citySlug || '')
            .replace(/\{productId\}/g, context.productId || '')
            .replace(/\{cityId\}/g, context.cityId || '')
            .replace(/\{cityName\}/g, context.cityName || '')
            .replace(/\{countryId\}/g, context.countryId || '')
            .replace(/\{checkIn\}/g, context.checkIn || '')
            .replace(/\{checkOut\}/g, context.checkOut || '')
            .replace(/\{adults\}/g, context.adults || '')
            .replace(/\{locale\}/g, context.locale || '')
            .replace(/\{currency\}/g, context.currency || '')
            .replace(/\{extraParams\}/g, context.extraParams || '');

        // Удаляем оставшиеся незамененные плейсхолдеры
        final = final.replace(/\{[^}]+\}/g, '');

        // Специальная обработка для Trip.com: убираем пустые параметры и barCurr, используем правильный домен
        if (provider.code === 'trip' && final.includes('trip.com')) {
            try {
                const url = new URL(final);

                // Убираем пустые параметры (значения пустые или только пробелы)
                const paramsToRemove = [];
                url.searchParams.forEach((value, key) => {
                    if (!value || value.trim() === '' || value === 'undefined' || value === 'null') {
                        paramsToRemove.push(key);
                    }
                });
                paramsToRemove.forEach(key => url.searchParams.delete(key));

                // Убираем barCurr (дубликат curr)
                url.searchParams.delete('barCurr');

                // Используем правильный домен для языка
                const langCode = context.locale?.split('-')[0]?.toLowerCase() || 'en';
                const domain = getTripDomainForLanguage(langCode);
                url.hostname = domain;

                // Добавляем партнёрские параметры из defaultParams (SID, allianceid, utm_campaign)
                if (defaultsObj && Object.keys(defaultsObj).length > 0) {
                    Object.entries(defaultsObj).forEach(([key, value]) => {
                        if (key && value != null) {
                            url.searchParams.set(key, String(value));
                        }
                    });
                }

                // Добавляем subId если есть
                if (subId) {
                    url.searchParams.set(subIdParam, subId);
                }

                final = url.toString();
            } catch (error) {
                // Если не удалось обработать URL, оставляем как есть
                if (process.env.NODE_ENV === 'development') {
                    console.warn('⚠️ Ошибка при обработке Trip.com шаблона:', error);
                }
            }
        } else {
            // Для других провайдеров добавляем партнёрские параметры и subId как обычно
            if (subId) {
                final = appendQueryParams(final, { [subIdParam]: subId });
            }
            // Для режима template также добавляем defaultParams, если они не были добавлены через шаблон
            if (defaultsObj && Object.keys(defaultsObj).length > 0 && !final.includes(Object.keys(defaultsObj)[0])) {
                final = appendQueryParams(final, defaultsObj);
            }
        }

        return wrap(final);
    }

    if (linkMode === 'redirectWrap') {
        // завернуть конечный URL в redirectBaseUrl
        const targetBase = baseUrl || trackingBaseUrl || '#';
        const target = appendQueryParams(targetBase, { ...defaultsObj, ...ctxObj });
        if (!redirectBaseUrl) return wrap(target);
        return wrap(`${redirectBaseUrl}${encodeURIComponent(target)}`);
    }

    // default: query – добавить параметры в ?query к baseUrl или trackingBaseUrl
    const targetBase = baseUrl || trackingBaseUrl || '#';
    const merged = { ...defaultsObj, ...ctxObj };
    // У Yesim в Sanity subIdParam = partner_id — тот же ключ, что и id партнёра в defaultParams:
    // метка страницы затирала partner_id=1197, и клик из таблиц и карточек статей партнёру не
    // засчитывался (ревью 30.09.2026). Метку не пишем поверх партнёрского параметра — страницу
    // и так несёт subid в /api/r.
    if (subId && !Object.prototype.hasOwnProperty.call(defaultsObj, subIdParam)) merged[subIdParam] = subId;
    return wrap(appendQueryParams(targetBase, merged));
}
